import { Injectable, inject } from '@angular/core';
import { BoxDetection, OverlayColor } from '@models';
import {
  CanvasService,
  DetectionService,
  HeatMapMetricsService,
  MessageService,
  RenderService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { ObjectoMetricsService } from './objecto-metrics.service';

const MIN_BOARD_RATIO = 1.2;
const HISTORY_SIZE = 5;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private canvasService = inject(CanvasService);
  private detectionService = inject(DetectionService);
  private objectoMetricsService = inject(ObjectoMetricsService);
  private renderService = inject(RenderService);
  private messageService = inject(MessageService);
  private heatMapMetricsService = inject(HeatMapMetricsService);

  cycleWorkerId: string = undefined;
  boardWorkerId: string = undefined;

  pegBoard = {} as BoxDetection;
  boardRatio = 0;

  triangleHistory: { left: number; right: number }[] = [];

  checkBoardHistory = 0;

  trianglesLeft = 0;
  trianglesRight = 0;

  addToHistory(triangles: { left: number; right: number }) {
    this.triangleHistory.push(triangles);
    if (this.triangleHistory.length > HISTORY_SIZE) {
      this.triangleHistory.shift();
    }
  }

  getHistoryAverage(): { left: number; right: number } {
    return {
      left: this.findMostFrequentValue(
        this.triangleHistory.map((triangle) => triangle.left),
      ),
      right: this.findMostFrequentValue(
        this.triangleHistory.map((triangle) => triangle.right),
      ),
    };
  }

  findMostFrequentValue(arr: number[]): number {
    const frequencyMap = new Map();
    let maxFrequency = 0;
    let mostFrequentValue = null;
    arr.forEach((value) => {
      const frequency = frequencyMap.get(value) || 0;
      const newFrequency = frequency + 1;
      frequencyMap.set(value, newFrequency);
      if (newFrequency > maxFrequency) {
        maxFrequency = newFrequency;
        mostFrequentValue = value;
      }
    });
    return mostFrequentValue;
  }

  placeRight(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.pegBoard = this.detectionService.getBox(
          'board',
          this.boardWorkerId,
        );

        const trianglesOnPeg = this.detectionService.getAllBoxInstances(
          'triangleOnPeg',
          this.cycleWorkerId,
        );

        if (this.pegBoard) {
          this.trianglesLeft = trianglesOnPeg.filter(
            (triangle) => triangle.center.x < this.pegBoard?.center.x,
          ).length;

          this.trianglesRight = trianglesOnPeg.filter(
            (triangle) => triangle.center.x > this.pegBoard?.center.x,
          ).length;

          this.addToHistory({
            left: this.trianglesLeft,
            right: this.trianglesRight,
          });

          if (this.trianglesLeft + this.trianglesRight !== 6) {
            this.trianglesLeft = this.getHistoryAverage().left;
            this.trianglesRight = this.getHistoryAverage().right;
          }
        }

        const boardRatio =
          (this.pegBoard?.max.x - this.pegBoard?.min.x) /
          (this.pegBoard?.max.y - this.pegBoard?.min.y);

        if (!boardRatio || boardRatio > MIN_BOARD_RATIO) {
          this.checkBoardHistory = 0;
          this.renderService.addStatic({
            id: 'moveToRight',
            overlay: {
              type: 'image',
              image: 'overlays/peg-transfer-left-to-right.svg',
              x: this.canvasService.convertCenterCoordinates({
                moveX: -60,
              }).x,
              y: 108,
            },
          });

          this.renderService.addStatic({
            id: 'counterLeft',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.blue,
              transparency: 0.3,
              x: this.canvasService.convertCenterCoordinates({
                moveX: -260,
              }).x,
              y: 108,
              radius: 28,
              value: this.trianglesLeft,
            },
          });

          this.renderService.addStatic({
            id: 'counterRight',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              x: this.canvasService.convertCenterCoordinates({
                moveX: 140,
              }).x,
              y: 108,
              radius: 28,
              value: this.trianglesRight,
            },
          });
        } else {
          this.checkBoardHistory++;
          if (this.checkBoardHistory > 5) {
            this.checkBoardHistory = 0;
            this.renderService.clearStaticOverlays();
          }
        }
        resolve(this.trianglesRight === 6);
      } catch (error) {
        reject(error);
      }
    });
  }

  async placeLeft(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.pegBoard = this.detectionService.getBox(
          'board',
          this.boardWorkerId,
        );

        const trianglesOnPeg = this.detectionService.getAllBoxInstances(
          'triangleOnPeg',
          this.cycleWorkerId,
        );

        if (this.pegBoard) {
          this.trianglesLeft = trianglesOnPeg.filter(
            (triangle) => triangle.center.x < this.pegBoard?.center.x,
          ).length;

          this.trianglesRight = trianglesOnPeg.filter(
            (triangle) => triangle.center.x > this.pegBoard?.center.x,
          ).length;

          this.addToHistory({
            left: this.trianglesLeft,
            right: this.trianglesRight,
          });

          if (this.trianglesLeft + this.trianglesRight !== 6) {
            this.trianglesLeft = this.getHistoryAverage().left;
            this.trianglesRight = this.getHistoryAverage().right;
          }
        }

        const boardRatio =
          (this.pegBoard?.max.x - this.pegBoard?.min.x) /
          (this.pegBoard?.max.y - this.pegBoard?.min.y);

        if (!boardRatio || boardRatio > MIN_BOARD_RATIO) {
          this.checkBoardHistory = 0;
          this.renderService.addStatic({
            id: 'moveToLeft',
            overlay: {
              type: 'image',
              image: 'overlays/peg-transfer-right-to-left.svg',
              x: this.canvasService.convertCenterCoordinates({
                moveX: -60,
              }).x,
              y: 108,
            },
          });

          this.renderService.addStatic({
            id: 'counterLeft2',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.green,
              transparency: 0.3,
              x: this.canvasService.convertCenterCoordinates({
                moveX: -260,
              }).x,
              y: 108,
              radius: 28,
              value: this.trianglesLeft,
            },
          });

          this.renderService.addStatic({
            id: 'counterRight2',
            overlay: {
              type: 'circle',
              fillColor: OverlayColor.blue,
              transparency: 0.3,
              x: this.canvasService.convertCenterCoordinates({
                moveX: 140,
              }).x,
              y: 108,
              radius: 28,
              value: this.trianglesRight,
            },
          });
        } else {
          this.checkBoardHistory++;
          if (this.checkBoardHistory > 5) {
            this.checkBoardHistory = 0;
            this.renderService.clearStaticOverlays();
          }
        }
        resolve(this.trianglesLeft === 6);
      } catch (error) {
        reject(error);
      }
    });
  }

  toolTracking() {
    this.objectoMetricsService.trackLeftTool(
      this.detectionService.getBox('toolLeft', this.cycleWorkerId),
    );
    this.objectoMetricsService.trackRightTool(
      this.detectionService.getBox('toolRight', this.cycleWorkerId),
    );
  }

  runHeatMapMetrics() {
    const canvas: HTMLCanvasElement = document.getElementById(
      'debugCanvas',
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    const toolLeft = this.detectionService.getBox(
      'toolLeft',
      this.cycleWorkerId,
    );

    const toolRight = this.detectionService.getBox(
      'toolRight',
      this.cycleWorkerId,
    );

    if (toolLeft) {
      this.heatMapMetricsService.updateToolPosition(
        toolLeft.center.x,
        toolLeft.center.y,
        'toolLeft',
        false,
        ctx,
      );
    }

    if (toolRight) {
      this.heatMapMetricsService.updateToolPosition(
        toolRight.center.x,
        toolRight.center.y,
        'toolRight',
        false,
        ctx,
      );
    }

    const messageToShow = this.heatMapMetricsService.generateMetricsReport();

    this.messageService.showDebugMessage(messageToShow);

    console.log(messageToShow);
  }
}
